import { lazy, Suspense } from 'react'
import { useParams, Route, Routes } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ResourceHeader, Spinner } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  DeviceCatalogRootPageDocument,
  GetFeatureAvailabilitiesDocument,
  ItAssetDetailsDocument
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import { buildNavigation } from 'modules/core/lib/react-router'

const DeviceVariantSelector = lazy(() =>
  import('modules/it_management/components/device_catalog/DeviceVariantSelector').then((m) => ({
    default: m.DeviceVariantSelector
  }))
)
const QuantityStepper = lazy(() =>
  import(
    'modules/it_management/components/device_catalog/MarketplaceCartSidepanel/QuantityStepper'
  ).then((m) => ({ default: m.QuantityStepper }))
)
const RequestDeviceModal = lazy(
  () => import('modules/it_management/components/device_catalog/RequestDeviceModal')
)
const DeviceCatalogPageLegacy = lazy(() => import('modules/it_management/pages/DeviceCatalogPage'))
const DeviceCatalogRootPageLegacy = lazy(
  () => import('modules/it_management/pages/DeviceCatalogRootPage')
)

const RootPage = lazy(() => import('modules/it_management/pages/RootPage'))
const DeviceCatalogRootPage = lazy(() => import('../pages/device_catalog/DeviceCatalogRootPage'))

const ItManagementIndexPage = lazy(() => import('../pages/index'))
const PeoplePage = lazy(() => import('../pages/PeoplePage'))
const DeviceCatalogPage = lazy(() => import('../pages/device_catalog/DeviceCatalogPage'))
const DeviceCatalogDetailPage = lazy(
  () => import('../pages/device_catalog/DeviceCatalogDetailPage')
)
const OrderPlanPage = lazy(() => import('../pages/device_catalog/OrderPlanPage'))
const OrderPlanSubmittedPage = lazy(() => import('../pages/device_catalog/OrderPlanSubmittedPage'))
const OrdersPage = lazy(() => import('../pages/device_catalog/OrdersPage'))
const UniversalImporterItAssetsModalPage = lazy(
  () => import('../pages/UniversalImporterItAssetsModalPage')
)
const UniversalImporterItAssetsResultPage = lazy(
  () => import('../pages/UniversalImporterItAssetsResultPage')
)
const EditItAssetPopup = lazy(() => import('../pages/EditItAssetPopup/index'))
const AssetDetailsLayout = lazy(() => import('../pages/AssetDetailsPage/AssetDetailsLayout'))
const AssetDetailsOverview = lazy(() => import('../pages/AssetDetailsPage/AssetDetailsOverview'))
const AssetDocumentsPage = lazy(() => import('../pages/AssetDetailsPage/AssetDocumentsPage'))

const DeviceCatalogRouteFallback = () => (
  <Box justifyContent="center" alignItems="center" height="full">
    <Spinner size="large" />
  </Box>
)

// eslint-disable-next-line no-constant-condition
if (false) {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  ;[
    DeviceVariantSelector,
    QuantityStepper,
    RequestDeviceModal,
    DeviceCatalogPageLegacy,
    DeviceCatalogRootPageLegacy
  ]
}

/** Keys detail page by variantId so it remounts on list→detail navigation and avoids stale state. */
const DeviceCatalogDetailRoute = () => {
  const { variantId } = useParams<{ variantId: string }>()
  return (
    <Suspense fallback={<DeviceCatalogRouteFallback />} key={variantId ?? 'none'}>
      <DeviceCatalogDetailPage />
    </Suspense>
  )
}

export const itManagementModuleRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.deviceCatalog.path,
      element: <DeviceCatalogRootPage />,
      handle: {
        module: 'device_catalog',
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: DeviceCatalogRootPageDocument
          }).map(({ data }) => {
            const canSeeDeviceCatalog = data?.itManagement._policies.sidebarSeeDeviceCatalogItem.can

            if (!canSeeDeviceCatalog) {
              return {
                title: ''
              }
            }

            return {
              title: i18n.t('it_management.device_catalog.title'),
              description: i18n.t('it_management.device_catalog.title')
            }
          })
        }
      },
      children: [
        {
          path: '',
          element: (
            <Suspense fallback={<DeviceCatalogRouteFallback />}>
              <DeviceCatalogPage />
            </Suspense>
          )
        },
        {
          path: navigation.deviceCatalog.orderPlan.path,
          element: (
            <Suspense fallback={<DeviceCatalogRouteFallback />}>
              <OrderPlanPage />
            </Suspense>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('it_management.device_catalog.order_plan.title')
            })
          }
        },
        {
          path: navigation.deviceCatalog.orderPlanSubmitted.path,
          element: (
            <Suspense fallback={<DeviceCatalogRouteFallback />}>
              <OrderPlanSubmittedPage />
            </Suspense>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('it_management.device_catalog.order_plan.submitted_title')
            })
          }
        },
        {
          path: navigation.deviceCatalog.orders.path,
          element: (
            <Suspense fallback={<DeviceCatalogRouteFallback />}>
              <OrdersPage />
            </Suspense>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('it_management.device_catalog.orders.title')
            })
          }
        },
        {
          path: navigation.deviceCatalog.variantId.path,
          element: <DeviceCatalogDetailRoute />,
          handle: {
            resource: true,
            // Do not run DistributorProductVariants here: with always-rendered layout the meta
            // would use api.local and fill the cache, so the page's federated useQuery would
            // get a cache hit and never hit the federated URL. The detail page runs the query
            // with the correct context and updates the header via useResourceHeader.
            meta: () => ({
              title: i18n.t('it_management.device_catalog.detail.title'),
              headerComponentProps: {
                title: i18n.t('it_management.device_catalog.detail.title'),
                metadata: []
              }
            })
          }
        }
      ]
    },
    {
      path: navigation.it_management.path,
      element: <RootPage />,
      handle: {
        module: 'it_management',
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: GetFeatureAvailabilitiesDocument,
            variables: {
              modules: [AvailableModules.it_management_v2],
              companyId: session.company.id
            }
          }).map(({ data }) => {
            const featuresData = data?.companies.featureAvailabilities ?? []
            const isItManagementEnabled = isFeatureAvailabilityEnabled(
              featuresData,
              Features.it_management_v2_core
            )

            // If user doesn't have permission, return minimal metadata (no title, no tabs)
            if (!isItManagementEnabled) {
              return {
                title: '',
                subNavigation: []
              }
            }

            return {
              title: i18n.t('it_management.it_inventory.title'),
              description: i18n.t('it_management.it_inventory.title'),
              settings: navigation.settings.it_inventory.fullPath(),
              subNavigation: [
                {
                  id: 'devices',
                  title: i18n.t('it_management.routes.devices'),
                  href: navigation.it_management.fullPath(),
                  index: true,
                  isVisible: true
                },
                {
                  id: 'people',
                  title: i18n.t('it_management.routes.people'),
                  href: navigation.it_management.fullPath() + '/people',
                  isVisible: true
                }
              ]
            }
          })
        }
      },
      children: [
        {
          path: '',
          element: <ItManagementIndexPage />,
          children: [
            {
              path: 'edit/:assetId',
              element: <EditItAssetPopup />
            }
          ]
        },
        {
          path: 'people',
          element: <PeoplePage />
        },
        {
          path: navigation.it_management.importAssetsResult.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterItAssetsResultPage />} />
            </Routes>
          )
        },
        {
          path: navigation.it_management.importAssets.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterItAssetsModalPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('it_management.routes.assets_universal_importer'),
              headerComponent: (
                <ResourceHeader title={i18n.t('it_management.routes.assets_universal_importer')} />
              )
            })
          }
        },
        {
          path: navigation.it_management.assetId.path,
          element: <AssetDetailsLayout />,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              return watchQuery({
                query: ItAssetDetailsDocument,
                variables: {
                  id: params.assetId || ''
                }
              }).map(({ data }) => {
                const asset = data?.itManagement?.itAsset
                const assetName = asset?.itAssetModel?.name || asset?.serialNumber
                return {
                  title: assetName || i18n.t('it_management.routes.asset_details')
                }
              })
            }
          },
          children: [
            {
              index: true,
              element: <AssetDetailsOverview />
            },
            {
              path: navigation.it_management.assetId.documents.path,
              element: <AssetDocumentsPage />
            }
          ]
        }
      ]
    }
  ]
})
